import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs, updateDoc, doc } from 'firebase/firestore';
import Layout from '../components/Layout';
import { logError } from '../utils/errorLogger';
import {
  Box, Typography, Paper, Tabs, Tab, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Button, Dialog,
  DialogTitle, DialogContent, DialogActions, Alert, CircularProgress,
  TextField, InputAdornment, Checkbox, FormControlLabel
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';

const ALL_ROLES = ['admin', 'coach', 'assistant_coach', 'volunteer', 'parent', 'adult_athlete', 'junior_athlete', 'tournament_admin', 'player'];

const ROLE_COLORS = {
  admin: { bg: '#FDEDEC', color: '#922B21' },
  coach: { bg: '#EBF5FB', color: '#1A5276' },
  assistant_coach: { bg: '#EBF5FB', color: '#1A5276' },
  volunteer: { bg: '#F4F6F7', color: '#555' },
  parent: { bg: '#E9F7EF', color: '#1E6B3C' },
  adult_athlete: { bg: '#FEF9E7', color: '#7B4800' },
  junior_athlete: { bg: '#F5EEF8', color: '#6C3483' },
  tournament_admin: { bg: '#EAF4FB', color: '#1A5276' },
  player: { bg: '#EBF5FB', color: '#1A5276' },
};

const ROLE_LABELS = {
  admin: 'Admin',
  coach: 'Coach',
  assistant_coach: 'Assistant Coach',
  volunteer: 'Volunteer',
  parent: 'Parent',
  adult_athlete: 'Adult Athlete',
  junior_athlete: 'Junior Athlete',
  tournament_admin: 'Tournament Admin',
  player: 'Player',
};

function Settings() {
  const [tab, setTab] = useState(0);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editDialog, setEditDialog] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editRoles, setEditRoles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchUsers = async () => {
      const snap = await getDocs(collection(db, 'users'));
      setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    };
    fetchUsers();
  }, []);

  const openEditDialog = (user) => {
    setEditUser(user);
    setEditRoles(user.roles || []);
    setError('');
    setEditDialog(true);
  };

  const toggleRole = (role) => {
    setEditRoles(prev =>
      prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]
    );
  };

  const handleSaveRoles = async () => {
    if (editRoles.length === 0) {
      setError('User must have at least one role.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await updateDoc(doc(db, 'users', editUser.id), { roles: editRoles });
      setUsers(prev => prev.map(u => u.id === editUser.id ? { ...u, roles: editRoles } : u));
      setEditDialog(false);
      setSuccess(`Roles updated for ${editUser.firstName} ${editUser.lastName}.`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      logError('Settings', err.message || String(err), err.stack || '');
      setError('Failed to update roles.');
    }
    setSaving(false);
  };

  const filteredUsers = users.filter(u => {
    const name = `${u.firstName || ''} ${u.lastName || ''} ${u.email || ''}`.toLowerCase();
    return name.includes(search.toLowerCase());
  });

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1E3A5F' }}>Settings</Typography>
          <Typography variant="body1" sx={{ color: '#888', mt: 0.5 }}>Manage roles and system configuration.</Typography>
        </Box>

        {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

        <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ borderBottom: '1px solid #eee', backgroundColor: '#f8f9fa' }}>
            <Tab label="Roles & Permissions" />
          </Tabs>

          {tab === 0 && (
            <Box>
              <Box sx={{ p: 2, borderBottom: '1px solid #eee' }}>
                <TextField
                  placeholder="Search users..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  size="small"
                  sx={{ width: 280 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#888' }} /></InputAdornment>
                  }}
                />
              </Box>

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#1E3A5F' }}>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Name</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Email</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Roles</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredUsers.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center" sx={{ py: 6, color: '#aaa' }}>No users found.</TableCell>
                        </TableRow>
                      ) : filteredUsers.map((u, i) => (
                        <TableRow key={u.id} sx={{ backgroundColor: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {u.firstName} {u.lastName}
                          </TableCell>
                          <TableCell sx={{ color: '#555' }}>{u.email}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {(u.roles || []).map(role => (
                                <Chip
                                  key={role}
                                  label={ROLE_LABELS[role] || role}
                                  size="small"
                                  sx={{
                                    backgroundColor: ROLE_COLORS[role]?.bg || '#f0f0f0',
                                    color: ROLE_COLORS[role]?.color || '#555',
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                  }}
                                />
                              ))}
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Button size="small" startIcon={<EditIcon />} onClick={() => openEditDialog(u)}>
                              Edit Roles
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </Paper>

        {/* Edit Roles Dialog */}
        <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ backgroundColor: '#1E3A5F', color: 'white' }}>
            Edit Roles — {editUser?.firstName} {editUser?.lastName}
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Typography variant="body2" sx={{ color: '#888', mb: 2 }}>
              Select all roles that apply to this user.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {ALL_ROLES.map(role => (
                <FormControlLabel
                  key={role}
                  control={
                    <Checkbox
                      checked={editRoles.includes(role)}
                      onChange={() => toggleRole(role)}
                      color="primary"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={ROLE_LABELS[role] || role}
                        size="small"
                        sx={{
                          backgroundColor: ROLE_COLORS[role]?.bg || '#f0f0f0',
                          color: ROLE_COLORS[role]?.color || '#555',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                        }}
                      />
                    </Box>
                  }
                />
              ))}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setEditDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveRoles} variant="contained" disabled={saving}
              sx={{ backgroundColor: '#1E3A5F' }}>
              {saving ? 'Saving...' : 'Save Roles'}
            </Button>
          </DialogActions>
        </Dialog>

      </Box>
    </Layout>
  );
}

export default Settings;
